export interface FeatureBillingData {
  productName: string;
  currentPeriodValue?: number;
  currentPeriodLimit?: number;
  pricePerUnit: number;
  numPeriodUpfront: number;
  upfrontPeriodsRemaining: number;
  numPeriodNoLimit: number;
  noLimitPeriodsRemaining: number;
  priceId: string;
  secondaryPriceId: string;
  usageChargeDescription?: string;
  priceStructure?: string;
  sku: string;
  secondarySku: string;
  nextPeriodValue?: number;
  type: 'PAY_BY_USAGE' | 'PAY_BY_SEAT';
  allowMinimumSeats?: number;
}

export class FeatureBilling {
  productName: string;
  currentPeriodValue?: number;
  currentPeriodLimit?: number;
  pricePerUnit: number;
  numPeriodUpfront: number;
  upfrontPeriodsRemaining: number;
  numPeriodNoLimit: number;
  noLimitPeriodsRemaining: number;
  priceId: string;
  secondaryPriceId: string;
  usageChargeDescription?: string;
  tiers: { upTo?: number; price: number }[];
  sku: string;
  secondarySku: string;
  nextPeriodValue: number;
  allowMinimumSeats: number;
  type: 'PAY_BY_USAGE' | 'PAY_BY_SEAT';

  constructor(data: FeatureBillingData) {
    this.productName = data.productName;
    this.currentPeriodValue = data.currentPeriodValue;
    this.currentPeriodLimit = data.currentPeriodLimit;
    this.pricePerUnit = data.pricePerUnit;
    this.numPeriodUpfront = data.numPeriodUpfront;
    this.upfrontPeriodsRemaining = data.upfrontPeriodsRemaining;
    this.numPeriodNoLimit = data.numPeriodNoLimit;
    this.noLimitPeriodsRemaining = data.noLimitPeriodsRemaining;
    this.priceId = data.priceId;
    this.secondaryPriceId = data.secondaryPriceId;
    this.usageChargeDescription = data.usageChargeDescription;
    this.sku = data.sku;
    this.secondarySku = data.secondarySku;
    this.type = data.type;
    this.allowMinimumSeats = data.allowMinimumSeats || 0;

    this.nextPeriodValue =
      data.type === 'PAY_BY_USAGE' ? 0 : data.nextPeriodValue || 0;

    if (data.priceStructure) {
      const ps = JSON.parse(data.priceStructure);
      this.tiers = ps.tiers;
    } else {
      this.tiers = [];
    }
  }

  isPricePrimary(priceId: string): boolean {
    return this.priceId === priceId;
  }

  isPriceSecondary(priceId: string): boolean {
    return this.secondaryPriceId === priceId;
  }

  getPrimaryFeatureUsage(): {
    included: number;
    used: number;
    remaining: number;
    billed: number;
    noLimitPeriodsRemaining: number;
  } | null {
    const res = {
      included: 0,
      used: 0,
      remaining: 0,
      billed: 0,
      noLimitPeriodsRemaining: 0
    };

    res.noLimitPeriodsRemaining = this.upfrontPeriodsRemaining ?? 0;
    res.remaining = res.noLimitPeriodsRemaining;
    res.included = this.numPeriodUpfront ?? 0;
    res.used = 1;

    res.billed = res.remaining === 0 ? 1 : 0;

    return res;
  }

  getSecondaryFeatureUsage(): {
    included: number;
    used: number;
    remaining: number;
    billed: number;
    noLimitPeriodsRemaining: number;
    usageChargeDescription?: string;
  } | null {
    const res = {
      included: 0,
      used: 0,
      remaining: 0,
      billed: 0,
      noLimitPeriodsRemaining: 0,
      usageChargeDescription: ''
    };

    res.usageChargeDescription = this.usageChargeDescription ?? '';
    res.noLimitPeriodsRemaining = this.noLimitPeriodsRemaining ?? 0;
    res.included = this.currentPeriodLimit ?? 0;
    res.used = this.currentPeriodValue ?? 0;
    res.remaining = Math.max(res.included - res.used, 0);
    res.billed =
      res.used > 0 && res.remaining === 0 ? res.used - res.included : 0;

    return res;
  }
}
